/* eslint-disable max-len */
import { ApiConfig } from './api';
import { fetchWrapper } from './utils/http-utils';
import { gzip } from '../libs';
import { PortfolioHoldings as AppPortfolioHoldings } from '../types/app-portfolio';
import { Submission } from '../types/batch';
import { PortfolioHoldings } from '../types/portfolio-holdings';

type SubmissionResponse = {
  message: string;
  submission_id: string;
};

// we will send this data compressed
export const universe_post_submission =
  (config: ApiConfig) =>
  (token: string, handle: string, data: Submission, batch: string = null): Promise<SubmissionResponse> => {
    const compressed = gzip(JSON.stringify(data));
    return fetchWrapper<SubmissionResponse>(
      `${config.userapiendpoint}universe/${handle}`,
      {
        method: 'POST',
        body: compressed,
        headers: {
          'Content-Type': 'application/octet-stream+json',
          'x-authtoken': token,
          batch: batch || undefined,
          'Content-Encoding': 'identity',
          'Gzip-Encoded': 'true',
          'Content-Length': compressed.length.toString(),
        },
      },
      config
    );
  };

export const signal_post_submission =
  (config: ApiConfig) =>
  (token: string, handle: string, data: Submission, batch: string = null): Promise<SubmissionResponse> => {
    const compressed = gzip(JSON.stringify(data));
    return fetchWrapper<SubmissionResponse>(
      `${config.userapiendpoint}signal/${handle}`,
      {
        method: 'POST',
        body: compressed,
        headers: {
          'Content-Type': 'application/octet-stream+json',
          'x-authtoken': token,
          batch: batch || undefined,
          'Content-Encoding': 'identity',
          'Gzip-Encoded': 'true',
          'Content-Length': compressed.length.toString(),
        },
      },
      config
    );
  };

export const signal_update_batch =
  (config: ApiConfig) =>
  (token: string, handle: string, batch: string): Promise<{ message: string; name: string }> =>
    fetchWrapper<{ message: string; name: string }>(
      `${config.userapiendpoint}signal/${handle}/batch?submit=true&name=${batch}`,
      {
        method: 'POST',
        headers: { 'x-authtoken': token },
      },
      config
    );

export const data_source_post_submission =
  (config: ApiConfig) =>
  (token: string, handle: string, data: Submission, batch: string = null): Promise<SubmissionResponse> => {
    const compressed = gzip(JSON.stringify(data));
    return fetchWrapper<SubmissionResponse>(
      `${config.userapiendpoint}data_source/${handle}`,
      {
        method: 'POST',
        body: compressed,
        headers: {
          'Content-Type': 'application/octet-stream+json',
          'x-authtoken': token,
          batch: batch || undefined,
          'Content-Encoding': 'identity',
          'Gzip-Encoded': 'true',
          'Content-Length': compressed.length.toString(),
        },
      },
      config
    );
  };

export const data_source_update_batch =
  (config: ApiConfig) =>
  (token: string, handle: string, batch: string): Promise<{ message: string; name: string }> =>
    fetchWrapper<{ message: string; name: string }>(
      `${config.userapiendpoint}data_source/${handle}/batch?submit=true&name=${batch}`,
      {
        method: 'POST',
        headers: { 'x-authtoken': token },
      },
      config
    );

// we will send this data compressed
export const portfolio_holdings_post_holdings =
  (config: ApiConfig) =>
  (token: string, holdingId: number, data: any): Promise<{ mapped_file?: string; unmapped_file?: string }> => {
    const compressed = gzip(JSON.stringify(data));
    return fetchWrapper<{ mapped_file?: string; unmapped_file?: string }>(
      `${config.userapiendpoint}portfolio_holdings/${holdingId}/holdings`,
      {
        method: 'POST',
        body: compressed,
        headers: {
          'Content-Type': 'application/octet-stream+json',
          'x-authtoken': token,
          'Content-Encoding': 'identity',
          'Gzip-Encoded': 'true',
          'Content-Length': compressed.length.toString(),
        },
      },
      config
    );
  };

export const app_portfolio_trigger_jobs =
  (config: ApiConfig) =>
  (token: string, handle: string, date: string, ignoreCache = false): Promise<any> => {
    return fetchWrapper(
      `${config.userapiendpoint}app_portfolio_holdings/${handle}/trigger-portfolio-jobs`,
      {
        method: 'POST',
        body: JSON.stringify({ date, ignore_cache: ignoreCache }),
        headers: {
          'x-authtoken': token,
        },
      },
      config
    );
  };

export const app_portfolio_holdings_trigger_jobs =
  (config: ApiConfig) =>
  (token: string, holdingsId: number): Promise<any> => {
    return fetchWrapper(
      `${config.userapiendpoint}app_portfolio_holdings/${holdingsId}/trigger-holdings-jobs`,
      {
        method: 'POST',
        headers: {
          'x-authtoken': token,
        },
      },
      config
    );
  };

// we will send this data compressed
export const app_portfolio_holdings_post_holdings =
  (config: ApiConfig) =>
  (token: string, holdingId: number, data: any): Promise<{ mapped_file?: string; unmapped_file?: string }> => {
    const compressed = gzip(JSON.stringify(data));
    return fetchWrapper<{ mapped_file?: string; unmapped_file?: string }>(
      `${config.userapiendpoint}app_portfolio_holdings/${holdingId}/holdings`,
      {
        method: 'POST',
        body: compressed,
        headers: {
          'Content-Type': 'application/octet-stream+json',
          'x-authtoken': token,
          'Content-Encoding': 'identity',
          'Gzip-Encoded': 'true',
          'Content-Length': compressed.length.toString(),
        },
      },
      config
    );
  };

// we will send this data compressed
export const backtest_portfolio_post_holdings =
  (config: ApiConfig) =>
  (token: string, holdingsId: number, data: any): Promise<{ mapped_file?: string; unmapped_file?: string }> => {
    const compressed = gzip(JSON.stringify(data));
    return fetchWrapper<{ mapped_file?: string; unmapped_file?: string }>(
      `${config.userapiendpoint}backtest/${holdingsId}/holdings`,
      {
        method: 'POST',
        body: compressed,
        headers: {
          'Content-Type': 'application/octet-stream+json',
          'x-authtoken': token,
          'Content-Encoding': 'identity',
          'Gzip-Encoded': 'true',
          'Content-Length': compressed.length.toString(),
        },
      },
      config
    );
  };

export const portfolio_multi_holdings =
  (config: ApiConfig) =>
  (
    token: string,
    handle: string,
    data: {
      column_map: Record<string, string>;
      csv: string;
      confirmed?: boolean;
      cash_identifier?: string;
      cash_value?: string;
      transactions_csv?: Record<string, string>[];
    }
  ): Promise<{ data: PortfolioHoldings[]; dates: string[] }> => {
    const compressed = gzip(JSON.stringify(data));
    return fetchWrapper<{ data: PortfolioHoldings[]; dates: string[] }>(
      `${config.userapiendpoint}portfolio_holdings/${handle}/multi-holdings`,
      {
        method: 'POST',
        body: compressed,
        headers: {
          'Content-Type': 'application/octet-stream+json',
          'x-authtoken': token,
          'Content-Encoding': 'identity',
          'Gzip-Encoded': 'true',
          'Content-Length': compressed.length.toString(),
        },
      },
      config
    );
  };

export const app_portfolio_multi_holdings =
  (config: ApiConfig) =>
  (
    token: string,
    handle: string,
    data: {
      column_map: Record<string, string>;
      csv: string;
      confirmed?: boolean;
      cash_identifier?: string;
      cash_value?: string;
      transactions_csv?: Record<string, string>[];
    }
  ): Promise<{ data: AppPortfolioHoldings[]; dates: string[] }> => {
    const compressed = gzip(JSON.stringify(data));
    return fetchWrapper<{ data: AppPortfolioHoldings[]; dates: string[] }>(
      `${config.userapiendpoint}app_portfolio_holdings/${handle}/multi-holdings`,
      {
        method: 'POST',
        body: compressed,
        headers: {
          'Content-Type': 'application/octet-stream+json',
          'x-authtoken': token,
          'Content-Encoding': 'identity',
          'Gzip-Encoded': 'true',
          'Content-Length': compressed.length.toString(),
        },
      },
      config
    );
  };

export const app_portfolio_holdings_delete =
  (config: ApiConfig) =>
  (token: string, id: number): Promise<{ message: string }> =>
    fetchWrapper<{ message: string }>(
      `${config.userapiendpoint}app_portfolio_holdings/delete/${id}`,
      {
        method: 'POST',
        headers: {
          'x-authtoken': token,
        },
      },
      config
    );
